import {
  useMutation,
  useQuery,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";
import { apiService } from "../services/api";
import { normalizeReason } from "../utils/familyAdminErrors";

export const CUSTOMER_FAMILY_QUERY_KEY = "customerFamily";

const isClientError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: unknown } } | null)
    ?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
};

/**
 * Family view of a customer, looked up by owner customer id, member id or
 * family id only. affiliate-manager-be rejects anything outside
 * [A-Za-z0-9_-]{1,64} with 400 INVALID_ID, so an e-mail never works here:
 * search by e-mail with searchFamilyAccounts or the customers search badge.
 */
export const useCustomerFamily = (
  customerId: string | null | undefined,
  enabled = true,
) => {
  return useQuery({
    queryKey: [CUSTOMER_FAMILY_QUERY_KEY, customerId],
    queryFn: () => apiService.getCustomerFamilyAccount(customerId as string),
    enabled: Boolean(customerId) && enabled,
    refetchOnWindowFocus: false,
    // A 4xx (404 FAMILY_NOT_FOUND, INVALID_ID) will not change on retry.
    retry: (failureCount, error) => !isClientError(error) && failureCount < 1,
    retryDelay: 1000,
  });
};

const invalidateAfterSuccess = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [CUSTOMER_FAMILY_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: ["customers"] });
  queryClient.invalidateQueries({ queryKey: ["customer"] });
};

// A conflict (INVITE_NOT_PENDING, MEMBER_NOT_TERMINAL) or a timeout means the
// shown family view may be stale, so it is refetched after a failure too.
const invalidateAfterError = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [CUSTOMER_FAMILY_QUERY_KEY] });
};

export interface FamilyMemberActionVariables {
  familyId: string;
  memberId: string;
  reason: string;
}

export interface FamilyActionVariables {
  familyId: string;
  reason: string;
}

export const useRemoveFamilyMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, memberId, reason }: FamilyMemberActionVariables) =>
      apiService.removeFamilyMember(familyId, memberId, normalizeReason(reason)),
    onSuccess: () => invalidateAfterSuccess(queryClient),
    onError: () => invalidateAfterError(queryClient),
  });
};

export const useCancelFamilyInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, memberId, reason }: FamilyMemberActionVariables) =>
      apiService.cancelFamilyInvitation(
        familyId,
        memberId,
        normalizeReason(reason),
      ),
    onSuccess: () => invalidateAfterSuccess(queryClient),
    onError: () => invalidateAfterError(queryClient),
  });
};

export const useDissolveFamily = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, reason }: FamilyActionVariables) =>
      apiService.dissolveFamily(familyId, normalizeReason(reason)),
    onSuccess: () => invalidateAfterSuccess(queryClient),
    onError: () => invalidateAfterError(queryClient),
  });
};

export const useResetFamilyMemberCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, memberId, reason }: FamilyMemberActionVariables) =>
      apiService.resetFamilyMemberCredential(
        familyId,
        memberId,
        normalizeReason(reason),
      ),
    onSuccess: () => invalidateAfterSuccess(queryClient),
    onError: () => invalidateAfterError(queryClient),
  });
};

export const useRemoveFamilyGoalImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, reason }: FamilyActionVariables) =>
      apiService.removeFamilyGoalImage(familyId, normalizeReason(reason)),
    onSuccess: () => invalidateAfterSuccess(queryClient),
    onError: () => invalidateAfterError(queryClient),
  });
};

export const useEraseFamilyMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, memberId, reason }: FamilyMemberActionVariables) =>
      apiService.eraseFamilyMember(familyId, memberId, normalizeReason(reason)),
    onSuccess: () => invalidateAfterSuccess(queryClient),
    onError: () => invalidateAfterError(queryClient),
  });
};
